import { Controller } from "@hotwired/stimulus"
import { Popover } from "bootstrap"

// Popover for ledger allocation details (hover) and reconcile form (click).
// Content lives in a <template> child — not in data-bs-content (breaks with HTML/forms).
// Click popovers get a close button and close on Escape. They are toggled by hand ("manual"):
// with Bootstrap's "click" trigger, a popover hidden programmatically needs two clicks to reopen.
export default class extends Controller {
  static targets = ["trigger", "content"]
  static values = {
    trigger: { type: String, default: "hover focus" },
    placement: { type: String, default: "left" },
    title: String,
    closeLabel: { type: String, default: "Fermer" }
  }

  connect() {
    const isHover = this.triggerValue.includes("hover")
    const isClick = this.triggerValue === "click"

    this.popover = new Popover(this.triggerTarget, {
      title: this.hasTitleValue ? this.titleValue : "",
      content: this.contentTarget.innerHTML,
      html: true,
      sanitize: false,
      trigger: isHover || isClick ? "manual" : this.triggerValue,
      placement: this.placementValue,
      container: "body",
      customClass: "ledger-allocation-popover shadow",
      fallbackPlacements: ["left", "right", "top", "bottom"]
    })

    if (isHover) this.bindHoverBehavior()
    if (isClick) this.bindClickBehavior()
  }

  disconnect() {
    clearTimeout(this.hideTimeout)
    document.removeEventListener("keydown", this.onKeydown)
    this.popover?.dispose()
    this.popover = null
  }

  bindClickBehavior() {
    this.onKeydown = (event) => {
      if (event.key === "Escape") this.popover?.hide()
    }

    this.triggerTarget.addEventListener("click", () => this.popover.toggle())

    this.triggerTarget.addEventListener("shown.bs.popover", () => {
      this.addCloseButton()
      document.addEventListener("keydown", this.onKeydown)
    })

    this.triggerTarget.addEventListener("hidden.bs.popover", () => {
      document.removeEventListener("keydown", this.onKeydown)
    })
  }

  addCloseButton() {
    const tip = this.popover?.tip
    if (!tip || tip.querySelector(".ledger-allocation-popover__close")) return

    const button = document.createElement("button")
    button.type = "button"
    button.className = "btn-close ledger-allocation-popover__close"
    button.setAttribute("aria-label", this.closeLabelValue)
    button.addEventListener("click", () => this.popover?.hide())
    tip.prepend(button)
  }

  bindHoverBehavior() {
    this.hideTimeout = null

    const show = () => {
      clearTimeout(this.hideTimeout)
      this.popover.show()
    }

    const scheduleHide = () => {
      this.hideTimeout = setTimeout(() => this.popover.hide(), 200)
    }

    this.triggerTarget.addEventListener("mouseenter", show)
    this.triggerTarget.addEventListener("mouseleave", scheduleHide)

    this.triggerTarget.addEventListener("shown.bs.popover", () => {
      const tip = this.popover.tip
      if (!tip) return

      tip.addEventListener("mouseenter", () => clearTimeout(this.hideTimeout))
      tip.addEventListener("mouseleave", scheduleHide)
    })
  }
}
